import { Box, Stack, StackProps, SxProps } from '@mui/material'
import { ReactNode, useCallback } from 'react'

import { MUIButton } from '../MUIButton'
import { MUICheckbox } from '../MUICheckBox'
import { MUIRadio } from '../MUIRadio'
import { Nullable } from 'models/helpers'
import { SELECTED_BLUE, WHITE } from 'constants/styling/theme'
import { SELECTION_RING_SX, useSelectionStyle } from '../SelectionStyle'
import classNames from 'classnames'
import styles from './SelectableButtonsGrid.module.sass'

export interface SelectableButtonsGridValue<T> {
  value: T
  displayName?: ReactNode
  // Optional param for values that should be displayed but cannot be selected
  disabled?: boolean
  // Optional icon to display above the text
  icon?: ReactNode
  // Optional font weight for the text
  fontWeight?: number | string
  /** data-cy of this value's button. */
  dataCy?: string
}

interface Props<T extends number | string = string> {
  /** The additional classes to append */
  className?: string
  /** The value for grid button to display */
  values: SelectableButtonsGridValue<T>[]
  /** Whether the active button responds to click or not */
  activeButtonClickable?: boolean
  /** The value of selected tab */
  selectedValue: Nullable<T | T[]>
  /** Whether checkbox should be displayed inside of buttons */
  showCheckbox?: boolean
  /** Whether Radio should be displayed inside of buttons */
  showRadio?: boolean
  /** Optional icons children to be displayed on the left of the grid button. */
  icons?: {
    [key in T]?: ReactNode
  }
  /** OnSelect action to be called */
  onSelect: (value: T) => void
  /** OnUnSelect action to be called */
  onUnselect?: (value: T) => void
  /** Prevent unselecting the only selected option */
  preventUnselect?: boolean
  /** Layout style for buttons - 'horizontal' (icon left of text) or 'vertical' (icon above text) */
  layout?: 'horizontal' | 'vertical'
  /** Default font weight for all buttons */
  defaultFontWeight?: number | string
  /** Whether buttons should be equal width and fit available space */
  equalWidth?: boolean
  /** Gap between buttons (default: 1) */
  gap?: number | string
  /** Whether to use nowrap for row layout (default: true for equalWidth, false otherwise) */
  noWrap?: boolean
  /** Additional styles for the buttons */
  sx?: SxProps
  /** Exposes the grid as a radio group (single choice) or a set of checkboxes, each button checked or not. */
  selectionRole?: 'radio' | 'checkbox'
  /** Accessible name of the group. */
  ariaLabel?: string
  /** Shows the values as a statement rather than a choice: no focus, no clicks. */
  readOnly?: boolean
  /** Lays the buttons out in a row (wrapping) or as a column; may be responsive. */
  direction?: StackProps['direction']
}

/**
 * @component SelectableButtonsGrid 
 * Displays grid of buttons for each provided value and highlights the selected ones.
 * This component can be assigned a generic type.
 * Handles both singular or plural selection.
 * @example
 * <SelectableButtonsGrid<ProductKind>
 *  values={mockValues}
 *  isActiveButtonDisabled={false}
 *  selectedValue={selectedValue}
 *  onSelect={value => setSelectedValue(value)}
 * />
 */
export const SelectableButtonsGrid = <T extends number | string = string>({
  className,
  values,
  activeButtonClickable = false,
  selectedValue,
  showCheckbox = false,
  showRadio = false,
  icons,
  onSelect,
  onUnselect,
  preventUnselect = false,
  layout = 'horizontal',
  defaultFontWeight = 400,
  equalWidth = false,
  gap = 1,
  noWrap = false,
  sx,
  selectionRole,
  ariaLabel,
  direction = 'row',
  readOnly = false,
}: Props<T>) => {
  const isRing = useSelectionStyle() === 'ring'

  /** Check if value is selected */
  const isValueSelected = useCallback((value: T) => {
    if (selectedValue === undefined || selectedValue === null) return false
    if (selectedValue instanceof Array) return selectedValue.includes(value)

    return selectedValue === value
  }, [selectedValue])

  const handleSelect = useCallback((value: T) => {
    if (!onSelect) return

    const isSelected = isValueSelected(value)

    // Prevent unselecting if it's the only selected option
    if (preventUnselect && isSelected && Array.isArray(selectedValue) && selectedValue.length === 1) return

    if (!isSelected) {
      onSelect(value)
      return
    }

    if (onUnselect) {
      onUnselect(value)
      return
    }

    return
  }, [onSelect, isValueSelected, onUnselect, selectedValue, preventUnselect])

  return (
    <Stack
      className={classNames(styles.selectableGrid, className)}
      direction={direction}
      flexWrap={direction !== 'row' || noWrap || equalWidth ? 'nowrap' : 'wrap'}
      gap={gap}
      sx={equalWidth ? { width: '100%' } : {}}
      role={selectionRole === 'radio' ? 'radiogroup' : selectionRole ? 'group' : undefined}
      aria-label={ariaLabel}
    >
      {values.map(({ value, displayName, disabled, icon, fontWeight, dataCy }) => {

        const isSelected = isValueSelected(value)
        const finalFontWeight = fontWeight ?? defaultFontWeight

        return (
          <MUIButton
            className={classNames(
              styles.selectableGridButton,
              {
                [styles.active]: isSelected,
                [styles.lockedActiveButton]: !activeButtonClickable,
                [styles.vertical]: layout === 'vertical'
              }
            )}
            key={value}
            disabled={disabled}
            type="secondaryBorder"
            endIcon={icons && icons[value]}
            onClick={() => !readOnly && handleSelect(value)}
            tabIndex={readOnly ? -1 : undefined}
            role={selectionRole}
            aria-checked={selectionRole ? isSelected : undefined}
            data-cy={dataCy}
            sx={{
              ...(equalWidth && { flex: 1 }),
              flexDirection: layout === 'vertical' ? 'column' : 'row',
              alignItems: 'center',
              justifyContent: 'center',
              height: layout === 'vertical' && equalWidth ? '10rem' : 'auto',
              minHeight: layout === 'vertical' ? '10rem' : 'auto',
              fontWeight: finalFontWeight,
              ...(readOnly && { pointerEvents: 'none' }),
              ...(isRing && {
                [`&&&.${styles.selectableGridButton}:hover`]: { backgroundColor: WHITE, borderColor: SELECTED_BLUE },
                [`&&&.${styles.active}`]: SELECTION_RING_SX,
              }),
              ...sx,
            }}
          >
            {showCheckbox && <MUICheckbox checked={isSelected} />}
            {showRadio && <MUIRadio checked={isSelected} />}

            {layout === 'vertical' && icon && (
              <Box sx={{ marginBottom: { xs: 0, lg: 1 }, marginTop: { xs: 0.8, lg: 0 }, display: 'flex', alignItems: 'center' }}>
                {icon}
              </Box>
            )}

            {layout === 'horizontal' && icon && (
              <Box sx={{ marginRight: 1, display: 'flex', alignItems: 'center' }}>
                {icon}
              </Box>
            )}

            <Box sx={{ textAlign: layout === 'vertical' ? 'center' : 'left', lineHeight: { xs: 1.1, lg: 1.9 } }}>
              {displayName ?? value}
            </Box>
          </MUIButton>
        )
      })}
    </Stack>
  )
}
